import { ArrowLeft, MoreHorizontal, X } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router'
import { Button } from '@/components/ui/button'
import { Chip } from '@/components/ui/chip'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Skeleton } from '@/components/ui/skeleton'
import { Surface } from '@/components/ui/surface'
import { PageContainer } from '@/components/PageContainer'
import { toastFailure } from '@/lib/feedback'
import { useVisualViewportBox } from '@/lib/useVisualViewport'
import { contactName } from './useRelationName'
import { getOrganisation } from '../organisations/api'
import { getCapture, processAndWait } from './api'
import { MIN_CHARS, NOTICE_VERSION } from './constants'
import { DictationField } from './DictationField'
import { DraftStatus } from './DraftStatus'
import { FirstUseNotice } from './FirstUseNotice'
import { OpenCaptures } from './OpenCaptures'
import { ProcessingView } from './ProcessingView'
import { ResultScreen } from './ResultScreen'
import { ReviewScreen } from './ReviewScreen'
import { deviceDictationLang, newestLocalDraft, rememberDictationLang } from './storage'
import { captureContext, type CaptureContext, type VoiceCapture } from './types'
import { useInsprekenStatus } from './useInsprekenStatus'
import { useNoticeGate } from './useNoticeGate'
import { useOfflineDraft, type DraftInitial } from './useOfflineDraft'
import { parseRelation, relationPath } from './relation'
import { useOnline } from './useOnline'

/** The relation the memo is about, as a chip that can be removed. */
function ContextChip({ context, onRemove }: { context: CaptureContext; onRemove: () => void }) {
  const [name, setName] = useState<string | null>(null)
  useEffect(() => {
    let alive = true
    const load =
      context.type === 'contact'
        ? contactName(context.id)
        : getOrganisation(context.id).then((organisation) =>
            'name' in organisation ? organisation.name : null,
          )
    load.then((found) => alive && setName(found)).catch(() => alive && setName(null))
    return () => {
      alive = false
    }
  }, [context.type, context.id])
  return (
    <Chip
      color="grijs"
      size="md"
      trailing={
        <button
          type="button"
          aria-label="Koppeling met deze relatie weghalen"
          className="grid size-6 place-items-center max-md:size-11"
          onClick={onRemove}
        >
          <X aria-hidden className="size-3.5" />
        </button>
      }
    >
      {name ?? (context.type === 'contact' ? 'Contact' : 'Organisatie')}
    </Chip>
  )
}

function Header({
  context,
  onRemoveContext,
  onLang,
  lang,
}: {
  context: CaptureContext | null
  onRemoveContext: () => void
  lang: 'nl-NL' | 'en-US'
  onLang: (lang: 'nl-NL' | 'en-US') => void
}) {
  const [menu, setMenu] = useState(false)
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <Link
          to={relationPath(context)}
          className="inline-flex min-h-11 items-center gap-1 text-sm font-medium text-primary hover:underline"
        >
          <ArrowLeft aria-hidden className="size-4" />
          {context ? 'Terug naar de relatie' : 'Terug naar Overzicht'}
        </Link>
        <Popover open={menu} onOpenChange={setMenu}>
          <PopoverTrigger asChild>
            <Button type="button" variant="ghost" size="icon" aria-label="Meer opties">
              <MoreHorizontal aria-hidden />
            </Button>
          </PopoverTrigger>
          <PopoverContent align="end" className="flex w-48 flex-col p-1">
            {(['nl-NL', 'en-US'] as const).map((option) => (
              <button
                key={option}
                type="button"
                aria-pressed={lang === option}
                className="rounded-md px-2 py-1.5 text-left text-sm hover:bg-muted max-md:min-h-11"
                onClick={() => {
                  setMenu(false)
                  onLang(option)
                }}
              >
                {option === 'nl-NL' ? 'Nederlands' : 'English'}
                {lang === option ? ' ✓' : ''}
              </button>
            ))}
          </PopoverContent>
        </Popover>
      </div>
      <h1 className="font-heading text-2xl font-bold">Inspreken</h1>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm text-muted-foreground">Over</span>
        {context ? (
          <ContextChip context={context} onRemove={onRemoveContext} />
        ) : (
          <Popover>
            <PopoverTrigger asChild>
              <Button type="button" variant="outline" size="sm">
                Kies of noem een relatie ▾
              </Button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-72 p-3 text-sm">
              Noem de naam in je verslag, bijvoorbeeld &ldquo;Ik sprak Jan Geert van Acme&rdquo;. Sparky zoekt
              de relatie en je controleert die voordat er iets wordt vastgelegd.
            </PopoverContent>
          </Popover>
        )}
      </div>
    </div>
  )
}

/** The memo screen: dictate or type, the concept saves itself, "Verwerken" sends it to Sparky. */
function Composer({
  initial,
  initialContext,
  noticeSeen,
}: {
  initial?: DraftInitial
  initialContext: CaptureContext | null
  noticeSeen: ReturnType<typeof useNoticeGate>
}) {
  const navigate = useNavigate()
  const online = useOnline()
  // A recovered local copy keeps the relation the memo was started from.
  const [context, setContext] = useState(
    () => initialContext ?? (initial ? null : (newestLocalDraft()?.draft.context ?? null)),
  )
  const [lang, setLang] = useState(deviceDictationLang)
  const [listening, setListening] = useState(false)
  const [processing, setProcessing] = useState(false)
  const stopRef = useRef<(() => void) | null>(null)
  const abortRef = useRef<AbortController | null>(null)
  const draft = useOfflineDraft({
    noticeVersion: NOTICE_VERSION,
    context,
    ...(initial ? { initial } : {}),
  })
  const viewport = useVisualViewportBox(true)
  const keyboardGap = viewport ? Math.max(0, window.innerHeight - viewport.offsetTop - viewport.height) : 0

  useEffect(() => () => abortRef.current?.abort(), [])

  const tooShort = draft.text.trim().length < MIN_CHARS
  const reason = tooShort ? 'Spreek iets meer in.' : !online ? 'Uit: geen verbinding' : null

  async function process() {
    if (reason) return
    setProcessing(true)
    if (listening) {
      stopRef.current?.()
      // The browser delivers the last words just after it is told to stop.
      await new Promise((resolve) => setTimeout(resolve, 400))
    }
    const id = await draft.flush()
    if (!id) {
      setProcessing(false)
      toastFailure(null, 'Het concept kon niet worden opgeslagen. Probeer het opnieuw.')
      return
    }
    const controller = new AbortController()
    abortRef.current = controller
    try {
      await processAndWait(id, { signal: controller.signal })
      navigate(`/relaties/inspreken/${id}`, { replace: true })
    } catch (caught) {
      if (controller.signal.aborted) return
      setProcessing(false)
      toastFailure(caught, 'Verwerken mislukt.')
    }
  }

  if (noticeSeen.needed) return <FirstUseNotice onUnderstood={noticeSeen.acknowledge} />

  return (
    <div className="space-y-5 pb-28 md:pb-0">
      <Header
        context={context}
        onRemoveContext={() => setContext(null)}
        lang={lang}
        onLang={(next) => {
          setLang(next)
          rememberDictationLang(next)
        }}
      />
      {processing ? (
        <ProcessingView offline={!online} />
      ) : (
        <>
          <p className="text-base text-muted-foreground">
            Vertel wat er besproken is: met wie, wat er is afgesproken en wat jij of een ander nog moet doen.
            Sparky maakt er voorstellen van; er wordt niets vastgelegd voordat jij het bevestigt.
          </p>
          <DictationField
            value={draft.text}
            onChange={draft.setText}
            onInputMethod={draft.setInputMethod}
            inputMethod={draft.inputMethod}
            lang={lang}
            onListeningChange={setListening}
            stopRef={stopRef}
          />
          <DraftStatus state={draft.state} />
          <OpenCaptures excludeId={draft.captureId ?? undefined} />
        </>
      )}
      {processing ? null : (
        <div
          style={{ '--keyboard-gap': `${keyboardGap}px` } as React.CSSProperties}
          className="fixed inset-x-0 bottom-[var(--keyboard-gap,0px)] z-20 space-y-1 border-t border-border bg-background p-3 md:static md:border-0 md:bg-transparent md:p-0"
        >
          {reason ? (
            <p id="verwerken-reden" className="text-sm text-muted-foreground">
              {reason}
            </p>
          ) : null}
          <Button
            className="w-full md:w-auto"
            size="lg"
            disabled={reason !== null}
            aria-describedby={reason ? 'verwerken-reden' : undefined}
            onClick={() => void process()}
          >
            {listening ? 'Verwerken (stopt eerst)' : 'Verwerken'}
          </Button>
        </div>
      )}
    </div>
  )
}

/** A capture opened by id: a concept goes back to the editor, a running one waits, a finished one is reviewed. */
function ExistingCapture({ id, noticeSeen }: { id: string; noticeSeen: ReturnType<typeof useNoticeGate> }) {
  const navigate = useNavigate()
  const online = useOnline()
  const [capture, setCapture] = useState<VoiceCapture | null>(null)
  const [failed, setFailed] = useState(false)
  const [version, setVersion] = useState(0)

  useEffect(() => {
    let alive = true
    getCapture(id)
      .then((found) => alive && setCapture(found))
      .catch(() => alive && setFailed(true))
    return () => {
      alive = false
    }
  }, [id, version])

  const running = capture?.status === 'verwerken'
  useEffect(() => {
    if (!running) return
    const controller = new AbortController()
    processAndWait(id, { signal: controller.signal })
      .then(() => setVersion((current) => current + 1))
      .catch(() => {
        if (!controller.signal.aborted) setFailed(true)
      })
    return () => controller.abort()
  }, [running, id])

  if (failed) {
    return (
      <Surface variant="page" padding="md" className="space-y-3">
        <p className="text-base">Dit verslag kon niet worden geladen.</p>
        <Button variant="outline" onClick={() => navigate('/relaties/inspreken')}>
          Nieuw verslag inspreken
        </Button>
      </Surface>
    )
  }
  if (!capture) return <Skeleton className="h-64 w-full rounded-3xl" />
  if (capture.status === 'concept') {
    return (
      <Composer
        key={capture.id}
        initialContext={captureContext(capture)}
        noticeSeen={noticeSeen}
        initial={{
          id: capture.id,
          clientCaptureId: capture.client_capture_id,
          text: capture.transcript ?? '',
          inputMethod: capture.input_method,
        }}
      />
    )
  }
  if (running) return <ProcessingView offline={!online} />
  if (capture.status === 'klaar' || capture.status === 'mislukt') {
    return (
      <ReviewScreen
        capture={capture}
        onCapture={setCapture}
        onConfirmed={(response) =>
          setCapture({
            ...capture,
            status: 'bevestigd',
            created_refs: response.refs,
            undo_until: response.undo_until || null,
          })
        }
      />
    )
  }
  if (capture.status === 'bevestigd' || capture.status === 'ongedaan') {
    return <ResultScreen capture={capture} onUndone={setCapture} />
  }
  return (
    <Surface variant="page" padding="md" className="space-y-2">
      <h2 className="text-lg font-semibold">Dit verslag is verlopen</h2>
      <p className="text-base text-muted-foreground">De tekst is na de bewaartermijn gewist.</p>
      <Button asChild variant="outline">
        <Link to="/relaties/inspreken">Nieuw verslag inspreken</Link>
      </Button>
    </Surface>
  )
}

/** `/relaties/inspreken` and `/relaties/inspreken/:id`. */
export function InsprekenPage() {
  const { id } = useParams()
  const [params] = useSearchParams()
  const { status, loading, available } = useInsprekenStatus()
  const noticeSeen = useNoticeGate(status)
  const context = parseRelation(params.get('relatie'))

  return (
    <PageContainer>
      {/* spec-70 readability exception: the editor keeps a 640 px, left-aligned column. */}
      <div className="w-full max-w-[640px]">
      {loading ? (
        <Skeleton className="h-64 w-full rounded-3xl" />
      ) : !available ? (
        <Surface variant="page" padding="md" className="space-y-3">
          <h1 className="text-lg font-semibold">Inspreken staat uit</h1>
          <p className="text-base text-muted-foreground">
            Je kunt een verslag nog wel zelf typen en bij de relatie vastleggen.
          </p>
          <Button asChild>
            <Link to={context ? relationPath(context) : '/relaties/contacten'}>Notitie vastleggen</Link>
          </Button>
        </Surface>
      ) : id ? (
        <ExistingCapture key={id} id={id} noticeSeen={noticeSeen} />
      ) : (
        <Composer initialContext={context} noticeSeen={noticeSeen} />
      )}
      </div>
    </PageContainer>
  )
}
